import { useNavigate } from "@tanstack/react-router";
import {
  AuthConfig,
  pluralize,
  queries,
  secondsInDay,
  useAuthConfig,
} from "@theprelude/core";
import { ReactNode } from "react";
import {
  convertDurationFilter,
  mfaStartTimeOdata,
} from "@/components/filter-form/additional-filters";
import { getOdataFilters } from "@/components/filter-form/odata";
import { createQuery } from "@/lib/create-query";
import { Filter } from "@/lib/filter";
import { useExceptedFilters } from "@/queries/control-monitor";
import { DonutChart } from "../donut-chart";
import { Card, getNavigationOptions, Metric } from "../sections";

export const ExtendedMFAChartCard = ({ days }: { days: number }) => {
  const filters = useMissingMFAFilters(days);
  const { showExcepted } = useExceptedFilters();

  const authConfig = useAuthConfig();
  const {
    data: { totalCount, controlFailureCount },
  } = mfaSummaryQuery(authConfig, filters, showExcepted).useSuspenseQuery();

  return (
    <MFASummaryCard
      label={`Users without MFA enabled in the last ${days} ${pluralize(days, "day")}`}
      count={controlFailureCount}
      total={totalCount}
      filters={filters}
    />
  );
};

const mfaSummaryQuery = (
  authConfig: AuthConfig,
  filters: Filter[],
  showExcepted: boolean,
) =>
  createQuery({
    queries: [
      queries.scm.summary(authConfig),
      queries.scm.summary(authConfig, {
        usersQuery: {
          filter: { and: getOdataFilters(filters) },
        },
      }),
    ],
    queryFn: (summary, filteredSummary) => ({
      totalCount: showExcepted
        ? summary.user_summary.user_count
        : summary.user_summary.excepted.user_count,
      controlFailureCount: showExcepted
        ? filteredSummary.user_summary.user_count
        : filteredSummary.user_summary.excepted.user_count,
    }),
  });

const useMissingMFAFilters = (days = 0): Filter[] => [
  convertDurationFilter(mfaStartTimeOdata, "less than", days * secondsInDay),
];

const MFASummaryCard = ({
  label,
  count,
  total,
  filters,
}: {
  label: ReactNode;
  count: number;
  total: number;
  filters: Filter[];
}) => {
  const navigate = useNavigate();

  return (
    <Card className="flex flex-col items-center gap-3">
      <Metric
        label={label}
        count={count}
        labelClassName="max-w-48 whitespace-normal text-center text-foreground-primary"
      />
      <DonutChart
        percentage={(count / total) * 100}
        onClick={() =>
          navigate(getNavigationOptions({ overlay: ["users", { filters }] }))
        }
      />
    </Card>
  );
};
